// src/pages/AudioQuizGame.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import confetti from 'canvas-confetti';
import { Home, RotateCcw, Trophy, Star, Volume2, VolumeX } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

const AudioQuizGame = () => {
  // Initialize hooks
  const { saveSessionData } = useAnalytics();
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    levelsData,
    bestScores,
    isLoading,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useFirebase('AudioQuizGame');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentLevelWords, setCurrentLevelWords] = useState([]);
  const [allLevelWords, setAllLevelWords] = useState([]);
  const [currentWord, setCurrentWord] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [answerOptions, setAnswerOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isLoadingLevel, setIsLoadingLevel] = useState(true);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);

  // Enhanced scoring system
  const [totalScore, setTotalScore] = useState(0);
  const [currentAttemptScore, setCurrentAttemptScore] = useState(0);
  const [timeBonus, setTimeBonus] = useState(0);
  const [streakBonus, setStreakBonus] = useState(0);
  const [accuracyBonus, setAccuracyBonus] = useState(0);
  const [stars, setStars] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [correctAttempts, setCorrectAttempts] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [levelStartTime, setLevelStartTime] = useState(Date.now());

  // UI state
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [celebrations, setCelebrations] = useState({});
  const [sparkles, setSparkles] = useState({});

  // Audio state - Auto-play capability
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const currentAudioRef = useRef(null);

  // Scoring constants
  const SCORING_CONSTANTS = {
    BASE_POINTS: 100,
    STREAK_BONUS: 50,
    TIME_BONUS_MAX: 50,
    ACCURACY_BONUS_MAX: 100,
    STAR_THRESHOLDS: {
      THREE: 85,
      TWO: 70,
      ONE: 50
    }
  };

  // Utility functions
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const resetLevelState = useCallback(() => {
    setCurrentWordIndex(0);
    setCurrentWord(null);
    setAnswerOptions([]);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setFeedback('');
    setTotalScore(0);
    setCurrentAttemptScore(0);
    setTimeBonus(0);
    setStreakBonus(0);
    setAccuracyBonus(0);
    setStars(0);
    setAttempts(0);
    setCorrectAttempts(0);
    setConsecutiveCorrect(0);
    setLevelStartTime(Date.now());
    setCelebrations({});
    setSparkles({});
    setShowLevelComplete(false);
    setLevelCompleteData(null);
  }, []);

  const calculateTimeBonus = useCallback((startTime, totalQuestions) => {
    const timeElapsed = (Date.now() - startTime) / 1000;
    const targetTime = totalQuestions * 10; // 10 seconds per question
    const bonusMultiplier = Math.max(0, (targetTime - timeElapsed) / targetTime);
    return Math.round(SCORING_CONSTANTS.TIME_BONUS_MAX * bonusMultiplier);
  }, []);

  const calculateAccuracyBonus = useCallback((correct, total) => {
    if (total === 0) return 0;
    const accuracy = correct / total;
    return Math.round(SCORING_CONSTANTS.ACCURACY_BONUS_MAX * accuracy);
  }, []);

  const calculateStars = useCallback((score, totalQuestions, accuracy) => {
    const maxPossibleScore = totalQuestions * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.TIME_BONUS_MAX + SCORING_CONSTANTS.ACCURACY_BONUS_MAX / totalQuestions);
    const scorePercentage = (score / maxPossibleScore) * 100;
    
    if (accuracy >= SCORING_CONSTANTS.STAR_THRESHOLDS.THREE && scorePercentage >= SCORING_CONSTANTS.STAR_THRESHOLDS.THREE) return 3;
    if (accuracy >= SCORING_CONSTANTS.STAR_THRESHOLDS.TWO && scorePercentage >= SCORING_CONSTANTS.STAR_THRESHOLDS.TWO) return 2;
    if (accuracy >= SCORING_CONSTANTS.STAR_THRESHOLDS.ONE && scorePercentage >= SCORING_CONSTANTS.STAR_THRESHOLDS.ONE) return 1;
    return 0;
  }, []);

  const triggerCelebration = useCallback((isStreak = false, starCount = 0) => {
    const id = Date.now();
    setCelebrations(prev => ({ ...prev, [id]: true }));
    
    if (starCount > 0) {
      Array.from({ length: starCount }, (_, i) => {
        setTimeout(() => playStarSound(), i * 200);
      });
    }
    
    if (isStreak) {
      playStreakSound();
      setSparkles(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setSparkles(prev => {
          const newSparkles = { ...prev };
          delete newSparkles[id];
          return newSparkles;
        });
      }, 2000);
    }
    
    setTimeout(() => {
      setCelebrations(prev => {
        const newCelebrations = { ...prev };
        delete newCelebrations[id];
        return newCelebrations;
      });
    }, 1500);
  }, [playStarSound, playStreakSound]);

  const generateWrongAnswers = useCallback((correctWord, count = 3) => {
    if (!allLevelWords || allLevelWords.length < count + 1) return [];
    
    const wrongAnswers = allLevelWords
      .filter(word => word.english !== correctWord.english)
      .sort(() => Math.random() - 0.5)
      .slice(0, count)
      .map(word => word.english);
    
    return wrongAnswers;
  }, [allLevelWords]);

  const setupCurrentQuestion = useCallback(() => {
    if (!currentLevelWords || currentWordIndex >= currentLevelWords.length) return;
    
    const word = currentLevelWords[currentWordIndex];
    const wrongAnswers = generateWrongAnswers(word, 3);
    const allAnswers = [word.english, ...wrongAnswers];
    const shuffledAnswers = shuffleArray(allAnswers);
    
    setCurrentWord(word);
    setAnswerOptions(shuffledAnswers);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setFeedback('');
  }, [currentWordIndex, currentLevelWords, generateWrongAnswers, shuffleArray]);

  // Auto-play audio when question appears
  const playWordAudio = useCallback(async () => {
    if (!currentWord?.audio) {
        console.error('No audio URL available');
        return;
    }

    try {
        // Clean up any existing audio
        if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        }

        setIsPlayingAudio(true);
        const audio = new Audio();
        currentAudioRef.current = audio;

        // Set up event listeners BEFORE setting src
        audio.addEventListener('loadeddata', () => {
        console.log('Audio loaded successfully');
        });

        audio.addEventListener('ended', () => {
        setIsPlayingAudio(false);
        });

        audio.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        console.error('Audio URL:', currentWord.audio);
        setIsPlayingAudio(false);
        });

        // Set the audio source
        audio.src = currentWord.audio;
        audio.load();

        // Attempt to play
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
        await playPromise;
        }

    } catch (error) {
        console.error('Error playing audio:', error);
        console.error('Audio URL:', currentWord.audio);
        setIsPlayingAudio(false);
    }
  }, [currentWord]);

  const handleAnswerSelect = useCallback((answer) => {
    if (isAnswered) return;

    setSelectedAnswer(answer);
    setIsAnswered(true);
    setAttempts(prev => prev + 1);

    const isCorrect = answer === currentWord.english;
    
    if (isCorrect) {
      setCorrectAttempts(prev => prev + 1);
      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      
      let points = SCORING_CONSTANTS.BASE_POINTS;
      
      if (newConsecutive >= 3) {
        const streakBonusPoints = SCORING_CONSTANTS.STREAK_BONUS;
        setStreakBonus(prev => prev + streakBonusPoints);
        points += streakBonusPoints;
      }
      
      setTotalScore(prev => prev + points);
      setCurrentAttemptScore(prev => prev + points);
      setFeedback('correct');
      
      const isStreak = newConsecutive >= 3;
      triggerCelebration(isStreak);
      
      if (!isStreak) {
        playCorrectSound();
      }
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_audio_answer',
        attempted: selectedAnswer,
        correct: currentWord.english,
        wordArabic: currentWord.arabic,
        wordIndex: currentWordIndex
      }]);
      setConsecutiveCorrect(0);
      setFeedback('incorrect');
      playIncorrectSound();
    }
  }, [isAnswered, currentWord, consecutiveCorrect, triggerCelebration, playCorrectSound, playIncorrectSound]);

  const handleNextQuestion = useCallback(() => {
    if (!isAnswered) return;

    playWhooshSound();
    
    setCurrentWordIndex(prev => {
      const newIndex = prev + 1;
      if (newIndex >= currentLevelWords.length) {
        handleLevelComplete();
        return prev;
      }
      return newIndex;
    });
  }, [isAnswered, currentLevelWords.length, playWhooshSound]);

  const handleLevelComplete = useCallback(() => {
    if (!currentLevelWords.length) return;

    // Calculate final scores
    const finalTimeBonus = calculateTimeBonus(levelStartTime, currentLevelWords.length);
    const accuracy = Math.round((correctAttempts / Math.max(1, attempts)) * 100);
    const finalAccuracyBonus = calculateAccuracyBonus(correctAttempts, attempts);
    const finalTotalScore = totalScore + finalTimeBonus + finalAccuracyBonus;
    const earnedStars = calculateStars(finalTotalScore, currentLevelWords.length, accuracy);

    // Update displayed scores
    setTimeBonus(finalTimeBonus);
    setAccuracyBonus(finalAccuracyBonus);
    setStars(earnedStars);
    setCurrentAttemptScore(finalTotalScore);

    const scoreData = {
      totalScore: finalTotalScore,
      stars: earnedStars,
      accuracy,
      timeBonus: finalTimeBonus,
      streakBonus,
      attempts
    };

    // Save the level data
    saveLevelData(level, scoreData);
    const sessionData = {
      gameType: 'audioQuizGame',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalTotalScore,
      accuracy,
      attempts,
      correctAnswers: correctAttempts,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      timeBonus: finalTimeBonus,
      streakBonus
    };
    saveSessionData(sessionData);

    // Prepare completion data
    const nextLevel = level + 1;
    const isLastLevel = !levelsData?.[nextLevel.toString()];
    
    // FIXED: Unlock next level immediately when level is completed (now uses fixed useFirebase)
    if (!isLastLevel && accuracy >= 70) { // Assuming 70% is passing grade
      console.log('Unlocking next level with accuracy:', accuracy);
      unlockNextLevel();
    }
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      levelTitle: levelsData[level.toString()]?.title || `Level ${level}`
    });

    // Show completion effects
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }

    playPerfectSound();
    confetti({
      particleCount: 200,
      spread: 100,
      origin: { y: 0.5 },
      colors: ['#FFD700', '#FFA500', '#FF6347'],
      shapes: ['star', 'circle']
    });
    
    setShowLevelComplete(true);
  }, [
    currentLevelWords.length,
    levelStartTime,
    correctAttempts,
    attempts,
    totalScore,
    streakBonus,
    level,
    levelsData,
    calculateTimeBonus,
    calculateAccuracyBonus,
    calculateStars,
    saveLevelData,
    unlockNextLevel,
    triggerCelebration,
    playPerfectSound
  ]);

  const handleProceedToNextLevel = useCallback(() => {
    if (!levelCompleteData) return;
    
    if (levelCompleteData.isLastLevel) {
      confetti({
        particleCount: 300,
        spread: 160,
        origin: { y: 0.4 },
        colors: ['#FFD700', '#FFA500', '#FF69B4', '#00CED1', '#32CD32'],
        shapes: ['star'],
        gravity: 0.6,
        drift: 0.2
      });
      alert("🎉 You've completed all levels! Amazing job!");
    } else {
      updateLevel(levelCompleteData.nextLevel);
      // REMOVED: unlockNextLevel() - now handled in handleLevelComplete
    }
    
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    resetLevelState();
    updateLevel(1);
    setShowResetConfirmation(false);
    setGamePhase('menu');
    playWhooshSound();
  }, [resetAllProgress, resetLevelState, updateLevel, playWhooshSound]);

  const selectLevel = useCallback((newLevel) => {
    updateLevel(newLevel);
  }, [updateLevel]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    resetLevelState();
    // Force setup of first question after reset
    setTimeout(() => {
      if (currentLevelWords.length > 0 && allLevelWords.length > 0) {
        setupCurrentQuestion();
      }
    }, 100);
  }, [resetLevelState, currentLevelWords, allLevelWords, setupCurrentQuestion]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  // Initialize level data when component mounts or level changes
  useEffect(() => {
    if (!levelsData || !level || !levelsData[level.toString()]) {
      setIsLoadingLevel(false);
      return;
    }
    
    const levelData = levelsData[level.toString()];
    if (!levelData?.wordPairs) {
      setIsLoadingLevel(false);
      return;
    }

    setIsLoadingLevel(true);
    
    try {
      // Transform wordPairs into the words format the game expects
      const transformedWords = levelData.wordPairs.map(pair => ({
        english: pair.english,
        arabic: pair.arabic,
        audio: pair.audio || pair.audioUrl,
        pronunciation: pair.pronunciation,
        emoji: pair.emoji
      }));

      const shuffledWords = shuffleArray([...transformedWords]);
      setCurrentLevelWords(shuffledWords);
      
      // Collect all words from all levels for wrong answer generation
      const allWords = [];
      Object.values(levelsData).forEach(lvl => {
        if (lvl.wordPairs) {
          allWords.push(...lvl.wordPairs.map(pair => ({
            english: pair.english,
            arabic: pair.arabic,
            audio: pair.audio || pair.audioUrl
          })));
        }
      });
      setAllLevelWords(allWords);
      
    } catch (error) {
      console.error('Error initializing level data:', error);
    } finally {
      setIsLoadingLevel(false);
    }
  }, [levelsData, level, shuffleArray]);

  // Set up current question when word index changes or when starting game
  useEffect(() => {
    if (isLoadingLevel || gamePhase !== 'playing') return;
    
    const dataReady = currentLevelWords.length > 0 && 
                     allLevelWords.length > 0 && 
                     currentWordIndex < currentLevelWords.length;

    if (dataReady) {
      setupCurrentQuestion();
    }
  }, [currentWordIndex, currentLevelWords, allLevelWords, setupCurrentQuestion, isLoadingLevel, gamePhase]);

  // Auto-play audio when question changes
  useEffect(() => {
    if (!currentWord?.audio || gamePhase !== 'playing') return;
    
    const timer = setTimeout(() => {
      playWordAudio();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentWord, playWordAudio, gamePhase]);

  // Start background music when component mounts
  useEffect(() => {
    if (userId) {
      setTimeout(startBackgroundMusic, 1000);
    }
  }, [userId, startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
        if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
        }
    };
  }, []);

  // Star Display Component
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => {
    return (
      <div className={`flex justify-center gap-1 ${size}`}>
        {[1, 2, 3].map(star => (
          <span 
            key={star} 
            className={`${star <= count ? 'text-yellow-400' : 'text-gray-300'} drop-shadow-lg`}
          >
            ⭐
          </span>
        ))}
      </div>
    );
  });

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  // Loading state
  if (isLoading || isLoadingLevel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border-4 border-yellow-300">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎧</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Audio Quiz...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for listening!</div>
          </div>
        </div>
      </div>
    );
  }

  if (!levelsData || !levelsData[level?.toString()]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full border-4 border-red-300">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <div className="text-red-600 text-xl md:text-2xl font-bold mb-4">No level data found!</div>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              🔄 Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentLevelData = levelsData[level.toString()];
  const accuracy = attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      {/* Audio Elements */}
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🎧 Sonic Safari 🎧
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Listen and match the Arabic words!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {currentLevelData.title || `Level ${level}`}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                Listen to Arabic words and choose the correct English translation!
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Words</p>
                  <p className="text-lg md:text-2xl font-black">{currentLevelWords.length}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎧</div>
                  <p className="font-bold text-sm md:text-lg">Game Type</p>
                  <p className="text-lg md:text-2xl font-black">Audio Quiz</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">70%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START LISTENING! 🚀
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-4">
                {Array.from({ length: Math.max(Object.keys(levelsData).length, maxLevel) }, (_, i) => i + 1).map(levelNum => {
                  const isUnlocked = levelNum <= maxLevel;
                  const isCurrent = levelNum === level;
                  const levelScore = bestScores[`level${levelNum}`];
                  
                  return (
                    <button
                      key={levelNum}
                      onClick={() => isUnlocked ? selectLevel(levelNum) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? `Level ${levelNum}${levelScore ? ` (Best: ${levelScore.bestScore || levelScore.totalScore || 0})` : ''}` : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{levelNum}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">Audio</div>
                      <div className="text-xs font-bold md:hidden">Audio</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔙 Main Menu
                </button>
                </Link>  
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
                <button 
                  onClick={() => setShowScoringHelp(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🏆 Scoring System
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Game Stats Box with Progress Bar */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-bold text-gray-700">Progress</span>
                  <span className="text-sm font-bold text-gray-700">
                    {currentWordIndex + (isAnswered ? 1 : 0)} / {currentLevelWords.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 border-2 border-gray-300">
                  <div 
                    className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${((currentWordIndex + (isAnswered ? 1 : 0)) / currentLevelWords.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{currentAttemptScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctAttempts}/{attempts || 1}</p>
                  <p className="text-xs font-bold">Correct</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{consecutiveCorrect}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">📊</div>
                  <p className="font-bold text-lg md:text-xl">{accuracy}%</p>
                  <p className="text-xs font-bold">Accuracy</p>
                </div>
              </div>
              
              {consecutiveCorrect >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {consecutiveCorrect} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Main Game Area */}
            <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 mb-6 border-4 border-yellow-300">
              {currentWord ? (
                <div className="text-center">
                  {/* Audio Player */}
                  <div className="mb-6 md:mb-8">
                    <div className="text-4xl md:text-6xl mb-4">🎵</div>
                    <p className="text-gray-700 font-bold text-lg md:text-xl mb-4 md:mb-6">Listen to the Arabic word:</p>
                    <button
                        onClick={playWordAudio}
                        disabled={isPlayingAudio}
                        className={`
                            px-6 md:px-8 py-3 md:py-4 rounded-2xl font-bold text-lg md:text-xl transition-all duration-200 shadow-xl border-4
                            ${isPlayingAudio 
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed border-gray-300' 
                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-2xl transform hover:scale-105 border-blue-300'
                            }
                        `}
                        aria-label={isPlayingAudio ? 'Playing audio...' : 'Play audio'}
                    >
                        {isPlayingAudio ? (
                        <>
                            <Volume2 className="inline mr-2" size={24} />
                            Playing... 🎧
                        </>
                        ) : (
                        <>
                            <VolumeX className="inline mr-2" size={24} />
                            🔊 Play Audio
                        </>
                        )}
                    </button>
                  </div>

                  {/* Arabic word display */}
                  <div className="mb-6 md:mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 md:mb-4">
                      What does this word mean in English?
                    </h2>
                    {currentWord.arabic && (
                      <p className="text-3xl md:text-4xl font-bold text-purple-600 mb-4" dir="rtl" lang="ar">
                        {currentWord.arabic}
                      </p>
                    )}
                  </div>

                  {/* Answer Options */}
                  <div className="mb-6 md:mb-8">
                    <p className="text-gray-700 font-bold text-lg md:text-xl mb-4 md:mb-6">
                      Choose the correct English translation:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {answerOptions.map((option, index) => {
                        let buttonClass = 'px-4 md:px-6 py-3 md:py-4 rounded-2xl font-bold text-base md:text-lg transition-all duration-200 shadow-lg border-4 ';
                        
                        if (isAnswered) {
                          if (option === currentWord.english) {
                            buttonClass += 'bg-gradient-to-r from-green-400 to-green-500 text-white border-green-300 transform scale-105 shadow-2xl';
                          } else if (option === selectedAnswer) {
                            buttonClass += 'bg-gradient-to-r from-red-400 to-red-500 text-white border-red-300 animate-pulse';
                          } else {
                            buttonClass += 'bg-gray-200 text-gray-500 border-gray-300';
                          }
                        } else {
                          buttonClass += 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border-purple-300 hover:from-purple-200 hover:to-pink-200 hover:shadow-xl transform hover:scale-105 cursor-pointer';
                        }

                        return (
                          <button
                            key={index}
                            onClick={() => handleAnswerSelect(option)}
                            disabled={isAnswered}
                            className={buttonClass}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Feedback */}
                  {feedback && (
                    <div className={`mb-6 md:mb-8 p-4 md:p-6 rounded-2xl font-bold text-lg md:text-xl border-4 ${
                      feedback === 'correct' 
                        ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300' 
                        : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300'
                    }`}>
                      {feedback === 'correct' ? (
                        <div>
                          <div className="text-3xl md:text-4xl mb-2">🎉</div>
                          <p>Excellent! That's correct!</p>
                          {currentWord.pronunciation && (
                            <p className="text-sm md:text-base mt-2 opacity-80">
                              Pronunciation: {currentWord.pronunciation}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="text-3xl md:text-4xl mb-2">☹️</div>
                          <p>Not quite! The correct answer is: <strong>{currentWord.english}</strong></p>
                          {currentWord.pronunciation && (
                            <p className="text-sm md:text-base mt-2 opacity-80">
                              Pronunciation: {currentWord.pronunciation}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Next Question Button */}
                  {isAnswered && (
                    <button
                      onClick={handleNextQuestion}
                      className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 border-4 border-emerald-300"
                    >
                      {currentWordIndex >= currentLevelWords.length - 1 ? "🎊 Complete Level!" : "Next Question →"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 md:py-12">
                  <div className="text-4xl md:text-6xl mb-4">🎧</div>
                  <div className="text-gray-600 text-lg md:text-xl font-bold">Loading next question...</div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={startLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {Object.values(celebrations).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {Object.values(sparkles).some(Boolean) && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      
      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-blue-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-600">📖 How to Play Audio Quiz!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6 text-sm md:text-base">
                <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                  <h3 className="font-bold text-blue-800 mb-2 flex items-center">
                    🎯 <span className="ml-2">Objective</span>
                  </h3>
                  <p className="text-blue-700">
                    Listen to Arabic words and choose the correct English translation from multiple choice options!
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-2xl border-2 border-green-200">
                  <h3 className="font-bold text-green-800 mb-2 flex items-center">
                    🎮 <span className="ml-2">How to Play</span>
                  </h3>
                  <div className="text-green-700 space-y-2">
                    <p><strong>1.</strong> Audio will automatically play when each question appears</p>
                    <p><strong>2.</strong> You'll see the Arabic word written on screen</p>
                    <p><strong>3.</strong> Listen carefully to the pronunciation</p>
                    <p><strong>4.</strong> Choose the correct English translation from 4 options</p>
                    <p><strong>5.</strong> Click "🔊 Play Audio" to hear the word again anytime</p>
                  </div>
                </div>
                
                <div className="p-4 bg-yellow-50 rounded-2xl border-2 border-yellow-200">
                  <h3 className="font-bold text-yellow-800 mb-2 flex items-center">
                    🎧 <span className="ml-2">Audio Features</span>
                  </h3>
                  <div className="text-yellow-700 space-y-2">
                    <p><strong>Auto-play:</strong> Audio plays automatically when each question loads</p>
                    <p><strong>Replay:</strong> Click the play button to hear the word again</p>
                    <p><strong>Pronunciation guide:</strong> Some words show pronunciation hints</p>
                    <p><strong>Visual + Audio:</strong> See the Arabic text while hearing it pronounced</p>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-50 rounded-2xl border-2 border-purple-200">
                  <h3 className="font-bold text-purple-800 mb-2 flex items-center">
                    📊 <span className="ml-2">Progress Tracking</span>
                  </h3>
                  <div className="text-purple-700 space-y-2">
                    <p><strong>Progress Bar:</strong> Shows how many questions you've completed</p>
                    <p><strong>Real-time Stats:</strong> Track your score, accuracy, and streak</p>
                    <p><strong>Streak Bonuses:</strong> Answer 3+ in a row for bonus points</p>
                    <p><strong>Level Completion:</strong> Finish all words to complete the level</p>
                  </div>
                </div>
                
                <div className="p-4 bg-red-50 rounded-2xl border-2 border-red-200">
                  <h3 className="font-bold text-red-800 mb-2 flex items-center">
                    ⚠️ <span className="ml-2">Scoring System</span>
                  </h3>
                  <div className="text-red-700 space-y-2">
                    <p><strong>Base Points:</strong> 100 points per correct answer</p>
                    <p><strong>Speed Bonus:</strong> Answer quickly for up to 50 bonus points</p>
                    <p><strong>Streak Bonus:</strong> 50 extra points for 3+ correct in a row</p>
                    <p><strong>Accuracy Bonus:</strong> High accuracy earns up to 100 bonus points</p>
                    <p><strong>Stars:</strong> Earn 1-3 stars based on performance (70%+ to unlock next level)</p>
                  </div>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-2xl border-2 border-orange-200">
                  <h3 className="font-bold text-orange-800 mb-2 flex items-center">
                    🏆 <span className="ml-2">Tips for Success</span>
                  </h3>
                  <div className="text-orange-700 space-y-2">
                    <p><strong>1.</strong> Listen to the audio carefully - pronunciation gives clues!</p>
                    <p><strong>2.</strong> Look at the Arabic text to recognize familiar letters/patterns</p>
                    <p><strong>3.</strong> Use the replay button if you need to hear the word again</p>
                    <p><strong>4.</strong> Work on building streaks for maximum points</p>
                    <p><strong>5.</strong> Don't rush - accuracy is more important than speed</p>
                    <p><strong>6.</strong> Pay attention to pronunciation guides when available</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Ready to Listen! 🎧
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Scoring Help Modal */}
      {showScoringHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">🏆 How Scoring Works!</h2>
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6 text-sm md:text-base">
                <div className="p-4 bg-green-100 rounded-2xl border-2 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2">💯 Base Points</h3>
                  <p className="text-green-700">Get {SCORING_CONSTANTS.BASE_POINTS} points for each correct answer!</p>
                </div>
                
                <div className="p-4 bg-orange-100 rounded-2xl border-2 border-orange-300">
                  <h3 className="font-bold text-orange-800 mb-2">🔥 Streak Bonus</h3>
                  <p className="text-orange-700">Answer 3+ questions in a row correctly to get +{SCORING_CONSTANTS.STREAK_BONUS} bonus points per question!</p>
                </div>
                
                <div className="p-4 bg-blue-100 rounded-2xl border-2 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2">⚡ Speed Bonus</h3>
                  <p className="text-blue-700">Complete the level quickly to earn up to {SCORING_CONSTANTS.TIME_BONUS_MAX} bonus points!</p>
                </div>
                
                <div className="p-4 bg-purple-100 rounded-2xl border-2 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2">🎯 Accuracy Bonus</h3>
                  <p className="text-purple-700">High accuracy earns up to {SCORING_CONSTANTS.ACCURACY_BONUS_MAX} bonus points!</p>
                </div>
                
                <div className="p-4 bg-yellow-100 rounded-2xl border-2 border-yellow-400">
                  <h3 className="font-bold text-yellow-800 mb-2">⭐ Stars</h3>
                  <div className="text-yellow-700 space-y-1">
                    <p>⭐ 1 Star: {SCORING_CONSTANTS.STAR_THRESHOLDS.ONE}%+ accuracy</p>
                    <p>⭐⭐ 2 Stars: {SCORING_CONSTANTS.STAR_THRESHOLDS.TWO}%+ accuracy</p>
                    <p>⭐⭐⭐ 3 Stars: {SCORING_CONSTANTS.STAR_THRESHOLDS.THREE}%+ accuracy</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 md:mt-8 text-center">
                <button 
                  onClick={() => setShowScoringHelp(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Got it! 👍
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-yellow-300">
            <div className="p-4 md:p-8 text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">🎉</div>
              <h2 className="text-2xl md:text-4xl font-bold text-purple-600 mb-3 md:mb-4">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <p className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </p>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-4xl md:text-6xl" />
              </div>
              
              <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8 text-sm md:text-base">
                <div className="bg-green-100 p-3 md:p-4 rounded-2xl border-2 border-green-300">
                  <p className="font-bold text-green-800">Total Score</p>
                  <p className="text-2xl md:text-3xl font-black text-green-800">{levelCompleteData.totalScore}</p>
                </div>
                <div className="bg-blue-100 p-3 md:p-4 rounded-2xl border-2 border-blue-300">
                  <p className="font-bold text-blue-800">Accuracy</p>
                  <p className="text-2xl md:text-3xl font-black text-blue-800">{levelCompleteData.accuracy}%</p>
                </div>
                <div className="bg-purple-100 p-3 md:p-4 rounded-2xl border-2 border-purple-300">
                  <p className="font-bold text-purple-800">Time Bonus</p>
                  <p className="text-xl md:text-2xl font-black text-purple-800">{levelCompleteData.timeBonus}</p>
                </div>
                <div className="bg-orange-100 p-3 md:p-4 rounded-2xl border-2 border-orange-300">
                  <p className="font-bold text-orange-800">Streak Bonus</p>
                  <p className="text-xl md:text-2xl font-black text-orange-800">{levelCompleteData.streakBonus}</p>
                </div>
              </div>
              
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                <button 
                  onClick={handleProceedToNextLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  {levelCompleteData.isLastLevel ? "🎊 Finish Game!" : `Next Level →`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-4xl md:text-6xl mb-4">⚠️</div>
              <h2 className="text-xl md:text-2xl font-bold text-red-600 mb-3 md:mb-4">Reset All Progress?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-base">
                This will delete all your scores, stars, and progress. You'll start from Level 1 again.
                This action cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioQuizGame;